import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Folder, Settings, LogOut, ExternalLink, CalendarDays, Briefcase } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { auth } from '../lib/api';
import { clearSession } from '../lib/auth';

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const chars = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  return (chars || name[0] || '?').toUpperCase();
};

// Single source of truth for what the profile-picture dropdown (desktop) and
// the expanded mobile menu (same links, listed flat) both show, so the two
// never drift apart.
export const getUserMenuLinks = (user) => {
  const isPhotographer = user?.role === 'photographer';
  const profileId = user?.photographer_profile?.id;

  if (isPhotographer) {
    return [
      {
        key: 'view-profile',
        label: 'View My Profile',
        to: profileId ? `/photographer/${profileId}` : '/photographer/dashboard',
        icon: ExternalLink,
        testid: 'menu-view-profile',
      },
      { key: 'bookings', label: 'Bookings', to: '/photographer/dashboard?tab=bookings', icon: CalendarDays, testid: 'menu-bookings' },
      { key: 'services', label: 'My Services', to: '/photographer/dashboard?tab=services', icon: Briefcase, testid: 'menu-services' },
      { key: 'profile', label: 'Profile', to: '/photographer/dashboard?tab=profile', icon: Settings, testid: 'menu-profile' },
    ];
  }

  return [
    { key: 'dashboard', label: 'Dashboard', to: '/client/dashboard', icon: LayoutDashboard, testid: 'menu-dashboard' },
    { key: 'vault', label: 'My Vault', to: '/vault', icon: Folder, testid: 'menu-vault' },
    { key: 'profile', label: 'Profile', to: '/client/dashboard?openProfile=1', icon: Settings, testid: 'menu-profile' },
  ];
};

// Profile-picture button that opens a role-aware dropdown (client vs
// photographer get different menus). Used on the public-facing pages
// (landing page, photographer listing, photographer profile) in place of
// plain "Dashboard" / "Log Out" buttons.
const UserMenu = ({ user, transparent = false }) => {
  const navigate = useNavigate();
  const links = getUserMenuLinks(user);

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch {
      // ignore - we're clearing the local session regardless
    }
    clearSession();
    navigate('/auth');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`rounded-full transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
            transparent
              ? 'ring-2 ring-white/70 hover:ring-white focus-visible:ring-white'
              : 'ring-2 ring-border hover:ring-primary/50 focus-visible:ring-primary'
          }`}
          data-testid="user-menu-trigger"
        >
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.avatar_path || undefined} alt={user?.name || 'Account'} />
            <AvatarFallback className="text-xs font-semibold">{initials(user?.name)}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-content">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none truncate">{user?.name}</p>
            <p className="text-xs leading-none text-muted-foreground truncate">{user?.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {links.map(({ key, label, to, icon: Icon, testid }) => (
          <DropdownMenuItem key={key} asChild data-testid={testid}>
            <Link to={to}>
              <Icon className="w-4 h-4 mr-2" />
              {label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive" data-testid="menu-logout">
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
